import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, MessageCircle, Search, Menu, X, ChevronDown, Download } from "lucide-react";
import { Logo } from "./Logo";
import { PHONE_DISPLAY, PHONE_TEL, whatsappLink, NAV_CATEGORIES, CATALOGUE_URL } from "../data/constants";

const MAIN_LINKS = [
  { label: "Home", to: "/" },
  { label: "About TFM", to: "/about" },
  { label: "Products", to: "/catalogue" },
  { label: "Wholesale", to: "/wholesale" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/catalogue?search=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="header-navigation-bar"
      className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 shadow-sm backdrop-blur-md"
    >
      <div className="bg-[#0B1B3D] text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 px-4 py-1.5 text-center sm:justify-between sm:px-6">
          <span data-testid="top-headline" className="font-spec text-[11px] font-semibold uppercase tracking-[0.15em] text-[#6EE7B7]">
            Premium Flooring &amp; Matting Solutions
          </span>
          <span className="hidden text-[11px] font-medium tracking-wide text-slate-300 sm:inline">
            Wholesale &bull; B2B &bull; Pan-India Delivery
          </span>
        </div>
      </div>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Logo />

        <nav className="hidden items-center gap-1 lg:flex">
          {MAIN_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-[#ECFDF5] hover:text-[#059669]"
            >
              {l.label}
            </Link>
          ))}
          <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
            <button
              data-testid="nav-categories-dropdown"
              className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-[#ECFDF5] hover:text-[#059669]"
            >
              Categories <ChevronDown className="h-4 w-4" />
            </button>
            {catOpen && (
              <div className="absolute right-0 top-full w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                {NAV_CATEGORIES.map((c) => (
                  <Link
                    key={c}
                    to={`/catalogue?category=${encodeURIComponent(c)}`}
                    data-testid={`nav-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-[#ECFDF5] hover:text-[#059669]"
                  >
                    {c}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <form onSubmit={submitSearch} className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="header-global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products"
              className="h-10 w-44 rounded-full border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none transition-all focus:w-56 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20"
            />
          </form>
        </div>

        <button
          className="lg:hidden rounded-lg p-2 text-[#0B1B3D]"
          onClick={() => setMobileOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-slate-200 bg-white px-4 py-4 lg:hidden">
          <form onSubmit={submitSearch} className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products"
              className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-[#10B981]"
            />
          </form>
          {MAIN_LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
              className="block rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-[#ECFDF5]">
              {l.label}
            </Link>
          ))}
          <div className="mt-2 border-t border-slate-100 pt-2">
            <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Categories</p>
            {NAV_CATEGORIES.map((c) => (
              <Link key={c} to={`/catalogue?category=${encodeURIComponent(c)}`} onClick={() => setMobileOpen(false)}
                className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-[#ECFDF5]">
                {c}
              </Link>
            ))}
          </div>
          <a href={CATALOGUE_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}
            className="mt-3 flex items-center justify-center gap-2 rounded-full border border-slate-200 py-2.5 text-sm font-semibold text-[#0B1B3D]">
            <Download className="h-4 w-4" /> Download Catalogue
          </a>
          <div className="mt-3 flex gap-2">
            <a href={PHONE_TEL ? `tel:${PHONE_TEL}` : "#"} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-200 py-2.5 text-sm font-semibold">
              <Phone className="h-4 w-4" /> Call
            </a>
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] py-2.5 text-sm font-bold text-white">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
